import {
	type MarketRegionResponseDto,
	OrderFieldTypeEnum,
	type OrderSetupDetailResponseDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { AddressInput } from "Shared/Components/Form/CustomFormComponent/AddressInput";
import Checkbox from "Shared/Components/Form/CustomFormComponent/FormCheckboxBoolean";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import TextEditor from "Shared/Components/Form/CustomFormComponent/TextEditor";
import { Grid2 } from "@mui/material";
import { useFormContext } from "react-hook-form";

const orderResources = Resources.base.order.editForm;

type GeneralEditFormProps = {
	orderSetup?: OrderSetupDetailResponseDto;
	marketRegionList?: MarketRegionResponseDto[];
	isLoadingMarketRegions?: boolean;
};

export function GeneralEditForm({
	orderSetup,
	marketRegionList,
	isLoadingMarketRegions,
}: GeneralEditFormProps) {
	const { t } = useResource();
	const {
		control,
		formState: { errors },
		getValues,
	} = useFormContext();
	const country = orderSetup?.market.country ?? undefined;

	return (
		<>
			<Grid2 container columnSpacing={2}>
				<Grid2 size={{ xs: 12 }}>
					<Select
						id="marketRegionId"
						label={t(orderResources.marketRegion)}
						fullWidth
						options={
							marketRegionList?.map((mr) => ({
								label: mr.name,
								value: mr.id,
							})) ?? []
						}
						variant="outlined"
						name="marketRegionId"
						control={control}
						errors={errors}
						isLoading={isLoadingMarketRegions}
						numeric
					/>
				</Grid2>

				{(getValues("fields")?.length ?? 0) > 0 &&
					[...(orderSetup?.orderFields ?? [])].map((field, index) => {
						return (
							<Grid2 key={field.key} size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
								{field.type === OrderFieldTypeEnum.boolean ? (
									<Checkbox
										id={field.key}
										name={`fields.${index}.value`}
										label={field.label}
										control={control}
										errors={errors}
									/>
								) : field.type === OrderFieldTypeEnum.string ? (
									<Input
										id={field.key}
										name={`fields.${index}.value`}
										label={field.label}
										variant="outlined"
										control={control}
										errors={errors}
										fullWidth
									/>
								) : field.type === OrderFieldTypeEnum.float ? (
									<Input
										id={field.key}
										name={`fields.${index}.value`}
										label={field.label}
										variant="outlined"
										control={control}
										errors={errors}
										fullWidth
										isFloat
										isNumber
									/>
								) : field.type === OrderFieldTypeEnum.integer ? (
									<Input
										id={field.key}
										name={`fields.${index}.value`}
										label={field.label}
										variant="outlined"
										control={control}
										errors={errors}
										fullWidth
										isNumber
									/>
								) : field.type === OrderFieldTypeEnum.enum ? (
									field.value && (
										<Select
											id={field.key}
											name={`fields.${index}.value`}
											label={field.label}
											variant="outlined"
											control={control}
											errors={errors}
											options={JSON.parse(field.value).map((option) => ({
												value: option.value,
												label: option.label,
											}))}
											fullWidth
										/>
									)
								) : null}
							</Grid2>
						);
					})}

				<Grid2 size={{ xs: 12 }}>
					<AddressInput
						id="address"
						name="address"
						label={t(orderResources.address)}
						control={control}
						errors={errors}
						fullWidth
						countryRestriction={country}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextEditor
						name="notes"
						label={t(orderResources.note)}
						control={control}
						errors={errors}
					/>
				</Grid2>
			</Grid2>
		</>
	);
}
